import { useState } from 'react'
import { ApiRequestError } from '../../../lib/api'
import type { ReviewProof } from '../../../stores/onboardingV2Store'
import { INPUT_CLASS } from './formStyles'

interface Props {
  proof: ReviewProof
  onEdit: (canonicalDescription: string) => Promise<void>
  onDiscard: () => Promise<void>
}

const FRAMING_NOTICE =
  'This is a neutral description we wrote. Your CV will phrase things differently for each role you apply to.'

function messageOf(err: unknown): string {
  if (err instanceof ApiRequestError) return err.error.message
  return 'Something went wrong — try again.'
}

export default function ReviewProofCard({ proof, onEdit, onDiscard }: Props) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(proof.canonical_description)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const save = async () => {
    if (!draft.trim()) return
    setBusy(true)
    setError(null)
    try {
      await onEdit(draft.trim())
      setEditing(false)
    } catch (err) {
      setError(messageOf(err))
    } finally {
      setBusy(false)
    }
  }

  const discard = async () => {
    setBusy(true)
    setError(null)
    try {
      await onDiscard()
    } catch (err) {
      setError(messageOf(err))
    } finally {
      setBusy(false)
    }
  }

  if (editing) {
    return (
      <div className="bg-ob-surface-card p-4">
        <label className="mb-1.5 block font-label text-[11px] uppercase tracking-wider text-ob-secondary">
          Description
        </label>
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          rows={4}
          className={`${INPUT_CLASS} resize-y`}
        />
        <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:justify-end">
          <button type="button" disabled={busy} onClick={() => { setDraft(proof.canonical_description); setEditing(false) }}
            className="font-body text-sm text-ob-secondary transition-colors hover:text-ob-ink disabled:opacity-50">
            Cancel
          </button>
          <button type="button" disabled={busy || !draft.trim()} onClick={() => void save()}
            className="border border-ob-ink px-4 py-1.5 font-body text-sm font-semibold text-ob-ink transition-colors hover:bg-ob-ink/5 disabled:opacity-50">
            {busy ? 'Saving…' : 'Save'}
          </button>
        </div>
        {error && (
          <p className="mt-2 font-body text-sm text-ob-error" role="alert">{error}</p>
        )}
      </div>
    )
  }

  return (
    <div className="bg-ob-surface-card p-4">
      <p className="font-body text-base leading-relaxed text-ob-on-surface">“{proof.canonical_description}”</p>
      {proof.competency_tags.length > 0 && (
        <p className="mt-3 font-body text-sm text-ob-secondary">
          Capabilities: {proof.competency_tags.join(', ')}
        </p>
      )}
      <p className="mt-3 border border-ob-secondary/20 bg-ob-surface px-3 py-2 font-body text-xs text-ob-secondary">
        {FRAMING_NOTICE}
      </p>
      <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:justify-end">
        <button type="button" disabled={busy} onClick={() => setEditing(true)}
          className="font-body text-sm font-semibold text-ob-ink transition-colors hover:text-ob-ink/70 disabled:opacity-50">
          Edit
        </button>
        <button type="button" disabled={busy} onClick={() => void discard()}
          className="font-body text-sm text-ob-secondary transition-colors hover:text-ob-error disabled:opacity-50">
          Looks wrong — discard
        </button>
      </div>
      {error && (
        <p className="mt-2 font-body text-sm text-ob-error" role="alert">{error}</p>
      )}
    </div>
  )
}
